import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ThumbsUp, Flag, Bookmark, Bell, CheckCircle2, Lock, Pin, Eye, Pencil, ArrowLeft, BookOpen } from "lucide-react";
import { API, cfg, errMsg, inp, ago, Badge, Avatar, Disclaimer, EscalationCTA } from "../lib/forumShared";
import { ReplyForm, ReplyTree } from "../components/forum/ForumReplies";

function PostActions({ p, me, act, onEdit }) {
  const btn = "inline-flex items-center gap-1 px-3 py-1.5 rounded-xl border text-[11px]";
  const report = async () => {
    const reason = window.prompt("Why are you reporting this discussion?");
    if (!reason) return;
    await axios.post(`${API}/forum/posts/${p.id}/report`, { reason }, cfg());
  };
  return (
    <div className="ml-auto flex flex-wrap gap-2">
      <button type="button" onClick={() => act(() => axios.post(`${API}/forum/posts/${p.id}/upvote`, {}, cfg()))} data-testid={`upvote-btn-${p.id}`} className={`${btn} ${me.voted ? "bg-[#0D3B2E] text-white border-[#0D3B2E]" : "border-gray-200"}`}>
        <ThumbsUp className="w-3.5 h-3.5" /> {p.upvotes || 0}
      </button>
      <button type="button" onClick={() => act(() => axios.post(`${API}/forum/posts/${p.id}/bookmark`, {}, cfg()), me.bookmarked ? "Bookmark removed" : "Bookmarked")} data-testid="forum-bookmark-btn" className={`${btn} ${me.bookmarked ? "bg-amber-50 border-amber-300 text-amber-800" : "border-gray-200"}`}>
        <Bookmark className="w-3.5 h-3.5" /> {me.bookmarked ? "Saved" : "Save"}
      </button>
      <button type="button" onClick={() => act(() => axios.post(`${API}/forum/posts/${p.id}/follow`, {}, cfg()), me.following ? "Unfollowed" : "You'll be notified of new replies")} data-testid="forum-follow-btn" className={`${btn} ${me.following ? "bg-[#EBF4F1] border-emerald-300 text-[#0D3B2E]" : "border-gray-200"}`}>
        <Bell className="w-3.5 h-3.5" /> {me.following ? "Following" : "Follow"}
      </button>
      {me.can_edit && <button type="button" onClick={onEdit} data-testid="forum-edit-btn" className={`${btn} border-gray-200`}><Pencil className="w-3.5 h-3.5" /> Edit</button>}
      <button type="button" onClick={() => act(report, "Reported to moderators")} data-testid={`report-btn-${p.id}`} className={`${btn} border-gray-200 text-gray-500 hover:text-rose-700`}><Flag className="w-3.5 h-3.5" /> Report</button>
    </div>
  );
}

function PostEditor({ ef, setEf, onSave, onCancel }) {
  return (
    <div className="space-y-2 text-xs">
      <input value={ef.title} onChange={(e) => setEf({ ...ef, title: e.target.value })} data-testid="post-edit-title" className={inp} />
      <textarea rows={6} value={ef.content} onChange={(e) => setEf({ ...ef, content: e.target.value })} data-testid="post-edit-content" className={inp} />
      <div className="flex gap-2">
        <button type="button" onClick={onSave} data-testid="post-edit-save" className="px-4 py-2 rounded-xl bg-[#0D3B2E] text-white font-bold">Save</button>
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-xl border">Cancel</button>
      </div>
    </div>
  );
}

function PostMeta({ p }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px]">
      <Link to={`/forum?category=${encodeURIComponent(p.category)}`} className="font-bold uppercase bg-[#EBF4F1] text-[#0D3B2E] px-2.5 py-0.5 rounded-full">{p.category}</Link>
      {p.is_pinned && <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><Pin className="w-3 h-3" /> Pinned</span>}
      {p.is_locked && <span className="inline-flex items-center gap-1 text-gray-500" data-testid="forum-locked"><Lock className="w-3 h-3" /> Locked — no new replies</span>}
      {p.status !== "published" && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold" data-testid="forum-post-status">{p.status}</span>}
      <span className="text-gray-400 ml-auto inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {p.views} · {ago(p.created_at)}</span>
    </div>
  );
}

function RelatedArticles({ items }) {
  if (!items?.length) return null;
  return (
    <div className="p-4 rounded-3xl bg-white border border-gray-200 space-y-2" data-testid="forum-related-articles">
      <p className="text-xs font-bold text-gray-900 inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> From the Health Library</p>
      {items.map((a) => (
        <Link key={a.id} to={`/health-library/${a.slug}`} className="block text-xs hover:text-[#0D3B2E]" data-testid={`forum-related-${a.id}`}>
          <b className="text-gray-900 block">{a.title}</b><span className="text-gray-500 line-clamp-2">{a.excerpt}</span>
        </Link>
      ))}
    </div>
  );
}

export default function ForumThreadPage() {
  const { id } = useParams(); const { user } = useAuth();
  const [p, setP] = useState(null); const [err, setErr] = useState(null); const [config, setConfig] = useState(null); const [editing, setEditing] = useState(false); const [ef, setEf] = useState({});
  const load = () => axios.get(`${API}/forum/posts/${id}`, cfg()).then((r) => { setP(r.data); setEf({ title: r.data.title, content: r.data.content, category: r.data.category }); }).catch((e) => setErr(errMsg(e, "Discussion not found")));
  useEffect(() => { load(); axios.get(`${API}/forum/config`).then((r) => setConfig(r.data)).catch(() => {}); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, ok) => { if (!user) return toast.info("Please log in to continue"); try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (err) return <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="forum-thread-missing"><p className="text-lg font-bold text-gray-900">{err}</p><Link to="/forum" className="text-sm underline font-bold text-[#0D3B2E]">Back to forum</Link></div>;
  if (!p) return <div className="max-w-5xl mx-auto px-4 py-10"><div className="h-40 bg-gray-100 rounded-3xl animate-pulse" /></div>;
  const me = p.me || {};
  const saveEdit = () => act(async () => { await axios.put(`${API}/forum/posts/${p.id}`, ef, cfg()); setEditing(false); }, "Post updated");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-10 space-y-5" data-testid="forum-thread">
      <Link to="/forum" className="inline-flex items-center gap-1 text-xs font-bold text-[#0D3B2E]" data-testid="forum-back"><ArrowLeft className="w-3.5 h-3.5" /> All discussions</Link>
      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-5">
          <article className="bg-white rounded-3xl border border-gray-200 p-5 sm:p-7 space-y-4" data-testid={`forum-post-${p.id}`}>
            <PostMeta p={p} />
            {editing ? <PostEditor ef={ef} setEf={setEf} onSave={saveEdit} onCancel={() => setEditing(false)} /> : (
              <>
                <h1 className="font-serif-luxury text-2xl sm:text-4xl font-bold text-gray-900 leading-tight">{p.title}</h1>
                <p className="text-sm text-gray-700 whitespace-pre-line break-words" data-testid="forum-post-content">{p.content}</p>
              </>
            )}
            {p.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {p.tags.map((t) => <Link key={t} to={`/forum?tag=${encodeURIComponent(t)}`} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBF9F4] border border-gray-200 text-gray-600" data-testid={`forum-tag-${t}`}>#{t}</Link>)}
              </div>
            )}
            <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-2"><Avatar name={p.author_name} /><b className="text-gray-900">{p.author_name}</b><Badge badge={p.author_badge} role={p.author_role} /></span>
              <PostActions p={p} me={me} act={act} onEdit={() => setEditing(!editing)} />
            </div>
          </article>
          {config && <Disclaimer text={config.disclaimer} compact />}
          {p.accepted_reply_id && <div className="text-xs font-bold text-emerald-800 inline-flex items-center gap-1" data-testid="forum-has-accepted"><CheckCircle2 className="w-4 h-4" /> This question has an accepted answer</div>}
          <section className="space-y-3" data-testid="forum-replies">
            <h2 className="font-bold text-gray-900 text-base md:text-lg">{p.replies_count || 0} {p.replies_count === 1 ? "reply" : "replies"}</h2>
            <ReplyTree post={p} me={me} user={user} onChange={load} />
          </section>
          <section className="bg-white rounded-3xl border border-gray-200 p-5" data-testid="forum-reply-section">
            {p.is_locked ? <p className="text-xs text-gray-500">This discussion is locked.</p>
              : user ? <ReplyForm postId={p.id} onDone={load} />
                : <p className="text-xs text-gray-600"><Link to="/login" className="font-bold underline" data-testid="forum-login-to-reply">Log in</Link> to reply or ask a follow-up.</p>}
          </section>
        </div>
        <aside className="space-y-4"><EscalationCTA /><RelatedArticles items={p.related_articles} /></aside>
      </div>
    </div>
  );
}
